<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import useGlobalStore from '@/store/globalStore';
import { setCustomConfig } from '@/api/client/hebeClient';
import {
  THEME_CODE,
  MATE_LANE_CODE,
  MATE_LANE_CODE_FX,
  MATE_STATS,
} from '@/enums';
import { setTheme, resMessage } from '@/utils/common';
import Cookies from 'js-cookie';
import { $alert, $message } from '@nio-fe/lego';
import useMatestoreStore from '@/store/matestoreStore';
import PrivateInfoDialog from '@/components/dev-support/PrivateInfoDialog.vue';

const THEME_CONSTANTS = [
  {
    code: THEME_CODE.auroraBlue,
    image: 'https://static.nio.com/fx-static/nio-mate-web/fixed/极光蓝.png',
    label: '极光蓝',
  },
  {
    code: THEME_CODE.midnightBlack,
    image: 'https://static.nio.com/fx-static/nio-mate-web/fixed/子夜黑.png',
    label: '子夜黑',
  },
  {
    code: THEME_CODE.cloudyGrey,
    image: 'https://static.nio.com/fx-static/nio-mate-web/fixed/云雾灰.png',
    label: '云雾灰',
  },
];

const globalStore = useGlobalStore();
const matestoreStore = useMatestoreStore();
const laneVal = ref<string>(Cookies.get(MATE_LANE_CODE) || '');
const laneValFX = ref<string>(Cookies.get(MATE_LANE_CODE_FX) || '');
const selectedMenuItem = ref('account_information');
const selectedTheme = ref('aurora-blue');
const statsOpened = ref(window.localStorage.getItem(MATE_STATS) === '1');
const layoutVersion = ref(Cookies.get('mate-layout') || 'v2');
const brandName = computed(() => {
  const brands = globalStore.getAvailableBrands();
  if (brands.length === 1 && brands[0] === 'alps') {
    return '乐道';
  } else {
    return '蔚来';
  }
});
const privateInfoDialogRef = ref();
onMounted(() => {
  selectedTheme.value = globalStore.theme;
});

const onSelectMenu = (value: string) => {
  selectedMenuItem.value = value;
  document.getElementById(value)?.scrollIntoView();
};

// 修改主题
const onSelectTheme = (newTheme: string) => {
  // theme code
  globalStore.theme = newTheme;
  setCustomConfig({
    module: 'titan_web',
    configs: [
      {
        type: 'theme_color',
        list: [
          {
            sort: 0,
            identifier: 'theme_color',
            biz_code: newTheme,
            structure: '',
            name: '',
          },
        ],
      },
    ],
  }).then((res) => {
    resMessage(res, '操作成功');
    setTheme(newTheme);
  });
};

const updatePassword = () => {
  window.location.href = globalStore.getGlobal?.endpoints?.password;
};

const onSubmit = (type: 'fx' | 'api') => {
  Cookies.set(
    type === 'fx' ? MATE_LANE_CODE_FX : MATE_LANE_CODE,
    type === 'fx' ? laneValFX.value : laneVal.value,
    {
      expires: 365,
    }
  );
  $message.success('保存成功');
};

const handleClearUserSetting = () => {
  matestoreStore.resetMicroAppSorting();
};
const handleShowPrivateInfo = () => {
  privateInfoDialogRef?.value?.openDialog();
};

// const handleSetUIVersion = () => {
//   layoutVersion.value = layoutVersion.value === 'v2' ? 'v1' : 'v2';
//   Cookies.set('mate-layout', layoutVersion.value, {
//     expires: 365,
//   });
//   $alert('切换成功，页面即将刷新').then(() => {
//     window.location.reload();
//   });
// };

const handleStatsToggle = (opened: boolean) => {
  localStorage.setItem(MATE_STATS, opened ? '1' : '0');
  $alert(`性能监控面板已${opened ? '打开' : '关闭'},页面刷新后生效`).then(
    () => {
      window.location.reload();
    }
  );
};
</script>

<template>
  <div class="titan-setting">
    <div class="titan-setting_header text-subtitle-1">{{ $t('setting') }}</div>
    <div class="titan-setting_content">
      <div class="tit-setting-bar">
        <n-menu :selected-value="selectedMenuItem" @select="onSelectMenu">
          <n-menu-item
            value="account_information"
            name="账户安全中心"
            icon="n-icon_security"
          />
          <n-menu-item
            value="general_setting"
            name="通用设置"
            icon="n-icon_settings_two"
          />
        </n-menu>
      </div>
      <div class="setting-content">
        <div class="account-info">
          <span class="text-subtitle-1" id="account_information">{{
            $t('account_information')
          }}</span>
          <div class="user-avatar">
            <n-avatar :src="globalStore.userInfo.avatar"></n-avatar>
            <div class="user-name-wrap">
              <div class="text-headline-2">
                {{ globalStore.domainDisplayName }}
              </div>
              <div class="nick-name text-body-3">
                {{ globalStore.userInfo?.nickName }}
              </div>
            </div>
          </div>
          <div class="setting-item">
            <div class="info-label">{{ $t('domain') }}：</div>
            <div class="info-value">
              {{
                globalStore.userInfo.domainAccount ||
                globalStore.userInfo.userName
              }}
            </div>

            <div class="info-label">个人号：</div>
            <div class="info-value">
              {{ globalStore.userInfo?.peopleWorkMobile ?? '--' }}
            </div>

            <div class="info-label">工作号：</div>
            <div class="info-value">
              {{
                globalStore.userInfo?.peopleWorkNio ??
                '请前往 Mate 员工管理进行维护！'
              }}
            </div>
            <n-divider />
            <div
              class="info-label bold-label"
              v-if="
                globalStore.userInfo?.accountPhoneNio ||
                globalStore.userInfo?.accountPhoneAlps ||
                globalStore.userInfo?.accountPhoneFy
              "
            >
              SSO绑定手机号
            </div>
            <template v-if="globalStore.userInfo?.accountPhoneNio">
              <div class="info-label">蔚来账号：</div>
              <div class="info-value">
                {{ globalStore.userInfo?.accountPhoneNio ?? '--' }}
              </div>
            </template>
            <template v-if="globalStore.userInfo?.accountPhoneAlps">
              <div class="info-label">乐道账号：</div>
              <div class="info-value">
                {{ globalStore.userInfo?.accountPhoneAlps ?? '--' }}
              </div>
            </template>
            <template v-if="globalStore.userInfo?.accountPhoneFy">
              <div class="info-label">萤火虫账号：</div>
              <div class="info-value">
                {{ globalStore.userInfo?.accountPhoneFy }}
              </div>
            </template>
          </div>
          <n-button type="secondary" size="mini" @click="updatePassword"
            >{{ $t('changing_account_password') }}
          </n-button>
        </div>
        <div class="general-setting">
          <div class="text-subtitle-1" id="general_setting">
            {{ $t('general_setting') }}
          </div>
          <div class="theme-text text-subtitle-3">{{ $t('theme_colors') }}</div>
          <div class="theme-wrap">
            <n-radio-group v-model="selectedTheme" @change="onSelectTheme">
              <div
                v-for="(theme, index) in THEME_CONSTANTS"
                :key="index"
                class="theme-item"
                :class="[
                  theme.code === selectedTheme ? 'theme-item_active' : '',
                ]"
              >
                <img :src="theme.image" />
                <div class="radio">
                  <n-radio :label="theme.code">{{ theme.label }}</n-radio>
                </div>
              </div></n-radio-group
            >
          </div>
        </div>
        <template
          v-if="['dev', 'test', 'stg'].includes(globalStore.getUserInfo.env)"
        >
          <div class="setting-grid">
            <div class="setting-item" style="margin-top: 20px">
              <div class="info-label">
                API Request lane参数(泳道参数 dev/test环境展示)
              </div>
              <div class="info-value">
                <n-input
                  v-model="laneVal"
                  placeholder="lane参数"
                  style="width: 200px"
                ></n-input>
                <n-button
                  @click="onSubmit('api')"
                  class="button"
                  size="small"
                  style="margin-left: 10px"
                  >保存</n-button
                >
              </div>
            </div>
            <div class="setting-item" style="margin-top: 20px">
              <div class="info-label">微应用（FX Pages） lane参数</div>
              <div class="info-value">
                <n-input
                  v-model="laneValFX"
                  placeholder="lane参数"
                  style="width: 200px"
                ></n-input>
                <n-button
                  @click="onSubmit('fx')"
                  class="button"
                  size="small"
                  style="margin-left: 10px"
                  >保存</n-button
                >
              </div>
            </div>
          </div>
          <div class="setting-item">
            <div class="info-label">开关性能监控面板</div>
            <div class="info-value">
              <n-button @click="handleStatsToggle(!statsOpened)"
                >{{ statsOpened ? '关闭' : '打开' }}性能监控面板</n-button
              >
            </div>
          </div>
        </template>

        <div class="setting-item">
          <div class="info-label">清除个人设置 (排序/常用小程序等)</div>
          <div class="info-value">
            <n-button
              v-popconfirm="{
                title: '确定要清除个人设置吗？',
                okText: '确定',
                okButtonProps: {
                  type: 'primary',
                  kind: 'negative',
                },
                cancelText: '取消',
                cancelButtonProps: {
                  type: 'text',
                  kind: 'neutral',
                },
              }"
              @click="handleClearUserSetting"
              size="small"
              >清除</n-button
            >
          </div>
        </div>
        <div class="setting-item">
          <div class="info-label">查看个人信息</div>
          <div class="info-value">
            <n-button @click="handleShowPrivateInfo" class="button" size="small"
              >查看</n-button
            >
          </div>
        </div>
      </div>
    </div>
  </div>
  <private-info-dialog ref="privateInfoDialogRef" />
</template>

<style lang="scss" scoped>
.titan-setting {
  height: calc(100% - 52px);
  .setting-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 16px;
    margin-top: 20px;
  }
  &_header {
    width: 100%;
    padding-left: 24px;
    padding-top: 24px;
    padding-bottom: 16px;
    line-height: 30px;
    border-bottom: 1px solid #f4f5f7;
  }
  .setting-item {
    margin-top: 32px;
  }
  .bold-label {
    font-weight: 600;
  }
  .info-label {
    margin-bottom: 8px;
    color: #73757a;
    span {
      font-size: 12px;
      color: #c0c0c0;
    }
  }
  .info-value {
    margin-bottom: 12px;
  }
  &_content {
    display: flex;
    height: calc(100% - 20px);
    .tit-setting-bar {
      align-items: center;
      width: 240px;
      height: 100%;
      padding: 8px;
      border-right: 1px solid #f4f5f7;
      .n-menu-box {
        height: 100%;
        width: auto !important;
      }
    }
    .setting-content {
      flex: 1;
      padding: 24px;
      .account-info {
        padding-bottom: 36px;
        margin-bottom: 40px;
        border-bottom: 1px solid #f4f5f7;
        .user-avatar {
          margin-top: 18px;
          display: flex;
          .user-name-wrap {
            margin-left: 18px;
            .nick-name {
              color: #73757a;
            }
          }
        }
      }
      .general-setting {
        .theme-text {
          margin-top: 20px;
          margin-bottom: 8px;
        }
        .theme-wrap {
          display: flex;
          .theme-item {
            width: 240px;
            height: 160px;
            overflow: hidden;
            margin-right: 18px;
            border: 0.5px solid #ced0d6;
            border-radius: 8px;
            /* shadow/shadow1_down */
            box-shadow: 0px 2px 4px 2px rgba(0, 0, 0, 0.04);
            &_active {
              background-color: #ecf8f9;
            }
            & > img {
              border-bottom: 1px solid #f4f5f7;
              background-color: #fff;
              width: 100%;
            }
            .radio {
              margin-left: 12px;
              margin-top: 5px;
            }
          }
        }
      }
    }
  }

  &_support {
  }
}
</style>

<style lang="scss">
.tit-setting-bar {
  .n-menu__item--selected > .n-menu__title {
    border: none !important;
    border-radius: 4px;
  }
}
</style>
